import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/useMobile";
import { CSSProperties, useEffect, useState } from "react";
import { Link } from "wouter";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { hasAnyRole } from "@/lib/roles";
import { AttendanceOverview } from "@/components/AttendanceOverview";
import { InstructorAttendanceReport } from "@/components/InstructorAttendanceReport";

export default function AdminReports() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user || !hasAnyRole(user.roles, ["admin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold">Zugriff verweigert</h1>
          <Link href="/">
            <Button>Zur Startseite</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <AdminReportsContent setSidebarWidth={setSidebarWidth} />
    </SidebarProvider>
  );
}

function AdminReportsContent({ setSidebarWidth }: { setSidebarWidth: (width: number) => void }) {
  const isMobile = useIsMobile();

  return (
    <>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Berichte</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground">Berichte</h1>
            <p className="text-muted-foreground">
              Statistiken und Auswertungen für Ihre Kurse
            </p>
          </div>

          {/* Attendance Overview - Full Version */}
          <AttendanceOverview />

          {/* Instructor Attendance Reports */}
          <div className="mt-6">
            <InstructorAttendanceReport />
          </div>

          {/* Future: Add more report sections here */}
          {/* 
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Export</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Export-Funktionen für Versicherungsberichte werden bald verfügbar sein.
              </p>
            </CardContent>
          </Card>
          */}
        </main>
      </SidebarInset>
    </>
  );
}
